import type { RegexPattern } from '../types';

/**
 * @group Constants
 * @summary <blockquote>Regex patterns for case conversion operations.</blockquote>
 * 
 * Patterns used to convert between different naming conventions (PascalCase, camelCase, kebab-case, etc.)
 */
export const CASE_CONVERSION_PATTERNS = {
  /**
   * Matches uppercase letters for PascalCase to kebab-case conversion
   * Example: "UserService" → "user-service"
   */
  PASCAL_TO_KEBAB_UPPERCASE: {
    regex: /([A-Z])/g,
    description: 'Matches uppercase letters for PascalCase to kebab-case conversion',
    examples: ['UserService', 'APIKey', 'HTTPSConnection'],
    counterExamples: ['user-service', 'api_key', 'https-connection'],
  } as RegexPattern,

  /**
   * Matches camelCase boundaries for kebab-case conversion
   * Example: "userName" → "user-name"
   */
  CAMEL_TO_KEBAB: {
    regex: /([a-z])([A-Z])/g,
    description: 'Matches camelCase boundaries for kebab-case conversion',
    examples: ['userName', 'apiKey', 'httpConnection'],
    counterExamples: ['user-name', 'API_KEY', 'HTTPConnection'],
  } as RegexPattern,

  /**
   * Matches spaces and underscores for hyphen replacement
   * Example: "user name" → "user-name", "user_name" → "user-name"
   */
  SPACES_UNDERSCORES_TO_HYPHEN: {
    regex: /[\s_]+/g,
    description: 'Matches spaces and underscores for hyphen replacement',
    examples: ['user name', 'user_name', 'api key'],
    counterExamples: ['user-name', 'username', 'apikey'],
  } as RegexPattern,

  /**
   * Matches leading hyphen to remove it
   * Example: "-user-service" → "user-service"
   */
  LEADING_HYPHEN: {
    regex: /^-/,
    description: 'Matches leading hyphen to remove it',
    examples: ['-user-service', '-api-key'],
    counterExamples: ['user-service', 'api-key'],
  } as RegexPattern,

  /**
   * Matches camelCase to snake_case conversion
   * Example: "userName" → "user_name"
   */
  CAMEL_TO_SNAKE: {
    regex: /([a-z])([A-Z])/g,
    description: 'Matches camelCase boundaries for snake_case conversion',
    examples: ['userName', 'apiKey', 'httpConnection'],
    counterExamples: ['user_name', 'API_KEY', 'HTTPConnection'],
  } as RegexPattern,

  /**
   * Matches PascalCase to snake_case conversion
   * Example: "UserService" → "user_service"
   */
  PASCAL_TO_SNAKE_UPPERCASE: {
    regex: /([A-Z])/g,
    description: 'Matches uppercase letters for PascalCase to snake_case conversion',
    examples: ['UserService', 'APIKey', 'HTTPSConnection'],
    counterExamples: ['user_service', 'api_key', 'https_connection'],
  } as RegexPattern,
} as const;
